import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  type Concepto,
  type Factura,
  downloadEdocta,
  getEdoctaDetalle,
} from "@/lib/api-edoctas";
import { cn, formatDate, formatMXN } from "@/lib/utils";

import { TelemetriaHorariaTab } from "./TelemetriaHorariaTab";

type Tab = "facturas" | "telemetria";

function isZeroConcepto(c: Concepto): boolean {
  const monto = Number(c.monto_total ?? 0);
  const iva = Number(c.iva ?? 0);
  const total = Number(c.total_neto ?? 0);
  return monto === 0 && iva === 0 && total === 0;
}

export function EdoctaDetail() {
  const { subcuenta = "", fecha = "" } = useParams<{ subcuenta: string; fecha: string }>();
  const [tab, setTab] = useState<Tab>("facturas");
  const [selectedFul, setSelectedFul] = useState<string | null>(null);
  const [hideZeros, setHideZeros] = useState<boolean>(true);

  const detalle = useQuery({
    queryKey: ["edocta-detalle", subcuenta, fecha],
    queryFn: () => getEdoctaDetalle(subcuenta, fecha),
    enabled: Boolean(subcuenta && fecha),
  });

  const conceptos = useMemo(
    () => detalle.data?.facturas.flatMap((f) => f.conceptos) ?? [],
    [detalle.data],
  );

  if (detalle.isLoading) {
    return <div className="text-slate-500">Cargando…</div>;
  }
  if (detalle.isError) {
    return <div className="text-red-600">Error: {(detalle.error as Error).message}</div>;
  }
  if (!detalle.data) return null;

  const c = detalle.data.cabecera;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link
          to="/reportes/edoctas"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4" /> Regresar
        </Link>
        <div className="flex gap-2">
          <DownloadBtn onClick={() => downloadEdocta(subcuenta, fecha, "pdf")} label="PDF" />
          <DownloadBtn onClick={() => downloadEdocta(subcuenta, fecha, "xml")} label="XML" />
        </div>
      </div>

      <div className="bg-white border rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Info label="Sistema" value={c.clv_sistema} />
        <Info label="Subcuenta" value={c.clv_subcuenta} />
        <Info label="Fecha operación" value={formatDate(c.fecha_oper)} />
        <Info label="Fecha emisión" value={formatDate(c.fecha_emis)} />
        <Info label="Archivo" value={c.file_name ?? "—"} mono />
        <Info label="Facturas" value={String(detalle.data.facturas.length)} />
        <Info label="Conceptos" value={String(conceptos.length)} />
        <Info label="Cargos" value={formatMXN(c.cargos)} />
        <Info label="Créditos" value={formatMXN(c.creditos)} />
        <Info label="Total neto" value={formatMXN(c.total_neto_dia)} strong />
      </div>

      <div className="border-b flex gap-1">
        <TabButton active={tab === "facturas"} onClick={() => setTab("facturas")}>
          Facturas
        </TabButton>
        <TabButton active={tab === "telemetria"} onClick={() => setTab("telemetria")}>
          Telemetría horaria
        </TabButton>
      </div>

      {tab === "facturas" && (
        <>
          <div className="flex items-center justify-between bg-white border rounded-lg px-4 py-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={hideZeros}
                onChange={(e) => setHideZeros(e.target.checked)}
                className="rounded border-slate-300"
              />
              Ocultar conceptos en $0
            </label>
            <span className="text-xs text-slate-500">
              {hideZeros
                ? `${conceptos.filter((co) => !isZeroConcepto(co)).length} de ${conceptos.length} conceptos visibles`
                : `${conceptos.length} conceptos`}
            </span>
          </div>
          <FacturasTab
            facturas={detalle.data.facturas}
            hideZeros={hideZeros}
            onVerTelemetria={(ful) => {
              setSelectedFul(ful);
              setTab("telemetria");
            }}
          />
        </>
      )}
      {tab === "telemetria" && (
        <TelemetriaHorariaTab
          conceptos={conceptos}
          selectedFul={selectedFul}
          onSelectFul={setSelectedFul}
        />
      )}
    </div>
  );
}

function FacturasTab({
  facturas,
  hideZeros,
  onVerTelemetria,
}: {
  facturas: Factura[];
  hideZeros: boolean;
  onVerTelemetria: (ful: string) => void;
}) {
  if (facturas.length === 0) {
    return (
      <div className="bg-white border rounded-lg px-4 py-8 text-center text-slate-500">
        Sin facturas.
      </div>
    );
  }

  // Filtrar facturas que se quedan sin conceptos después del filtro.
  const visibles = hideZeros
    ? facturas.filter((f) => f.conceptos.some((co) => !isZeroConcepto(co)))
    : facturas;

  if (visibles.length === 0) {
    return (
      <div className="bg-white border rounded-lg px-4 py-8 text-center text-slate-500">
        Todas las facturas tienen conceptos en $0. Desactiva el filtro para verlas.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {visibles.map((f) => (
        <FacturaCard
          key={f.fuf}
          factura={f}
          hideZeros={hideZeros}
          onVerTelemetria={onVerTelemetria}
        />
      ))}
    </div>
  );
}

function FacturaCard({
  factura,
  hideZeros,
  onVerTelemetria,
}: {
  factura: Factura;
  hideZeros: boolean;
  onVerTelemetria: (ful: string) => void;
}) {
  const subtotal = factura.conceptos.reduce(
    (acc, co) => acc + Number(co.total_neto ?? 0),
    0,
  );
  const conceptos = hideZeros
    ? factura.conceptos.filter((co) => !isZeroConcepto(co))
    : factura.conceptos;
  const hiddenCount = factura.conceptos.length - conceptos.length;

  return (
    <div className="bg-white border rounded-lg">
      <div className="px-4 py-3 border-b flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium">
            Liq. {factura.num_liq} · {factura.razon_social ?? factura.emisor ?? "—"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {factura.tipo && <span>{factura.tipo} · </span>}
            {factura.rfc ?? "—"}
            {factura.fecha_pago && <span> · Pago: {formatDate(factura.fecha_pago)}</span>}
            {factura.ref_bancaria && <span> · Ref: {factura.ref_bancaria}</span>}
            {hideZeros && hiddenCount > 0 && (
              <span> · {hiddenCount} ocultos en $0</span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-slate-500">Subtotal</div>
          <div className="font-semibold tabular-nums">{formatMXN(subtotal)}</div>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">Grupo</th>
            <th className="px-3 py-2 font-medium">Descripción</th>
            <th className="px-3 py-2 font-medium text-right">Monto</th>
            <th className="px-3 py-2 font-medium text-right">IVA</th>
            <th className="px-3 py-2 font-medium text-right">Total neto</th>
            <th className="px-3 py-2 font-medium text-right"></th>
          </tr>
        </thead>
        <tbody>
          {conceptos.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-3 text-center text-slate-400 text-xs">
                Sin conceptos.
              </td>
            </tr>
          )}
          {conceptos.map((co) => (
            <ConceptoRow key={co.ful} concepto={co} onVerTelemetria={onVerTelemetria} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConceptoRow({
  concepto,
  onVerTelemetria,
}: {
  concepto: Concepto;
  onVerTelemetria: (ful: string) => void;
}) {
  return (
    <tr className="border-t">
      <td className="px-3 py-2 text-slate-700">{concepto.grupo ?? "—"}</td>
      <td className="px-3 py-2 text-slate-600">{concepto.descripcion ?? "—"}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatMXN(concepto.monto_total)}</td>
      <td className="px-3 py-2 text-right tabular-nums">{formatMXN(concepto.iva)}</td>
      <td className="px-3 py-2 text-right tabular-nums font-medium">
        {formatMXN(concepto.total_neto)}
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          className="text-xs text-blue-600 hover:underline"
          onClick={() => onVerTelemetria(concepto.ful)}
        >
          Ver 24h →
        </button>
      </td>
    </tr>
  );
}

function Info({
  label,
  value,
  strong = false,
  mono = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div
        className={cn(
          "mt-0.5",
          strong && "text-lg font-semibold tabular-nums",
          mono && "font-mono text-xs",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-4 py-2 text-sm border-b-2 -mb-px",
        active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500",
      )}
    >
      {children}
    </button>
  );
}

function DownloadBtn({ onClick, label }: { onClick: () => Promise<void>; label: string }) {
  return (
    <button
      type="button"
      onClick={() => onClick().catch((err) => alert(`Error al descargar: ${err.message}`))}
      className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm hover:bg-slate-100"
    >
      <Download className="w-4 h-4" />
      {label}
    </button>
  );
}
